import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { Heart, Plus, Edit, X, AlertCircle, Camera } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { mascotaService } from '../services/mascotaService'
import { usuarioService } from '../services/usuarioService'
import LoadingState from '../components/ui/LoadingState'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Listbox from '../components/ui/Listbox'
import { normalizeFotoUrl } from '../utils/avatarHelpers'

function normalizeDateToInput(value) {
  if (!value) return ''
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toISOString().split('T')[0]
  } catch {
    return ''
  }
}

function getMascotaId(m) {
  return m?.id_mascota ?? m?.idMascota
}

export default function EditarMascota() {
  const navigate = useNavigate()
  const { id } = useParams()
  const mascotaId = id
  const { user } = useAuth()
  const { isDueno, isAdmin } = usePermissions()
  const fileInputRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [mascota, setMascota] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [deletingAvatar, setDeletingAvatar] = useState(false)
  const [duenos, setDuenos] = useState([])
  const [loadingDuenos, setLoadingDuenos] = useState(false)

  const [formData, setFormData] = useState({
    nombre: '',
    especie: '',
    raza: '',
    tipo_raza: '',
    fecha_nacimiento: '',
    sexo: '',
    color: '',
    peso: '',
    altura: '',
    microchip: '',
    observaciones: '',
    origen: '',
    criadero: '',
    fecha_adopcion: '',
    id_dueño: ''
  })

  const myId = user?.idUsuario ?? user?.id_usuario

  const canManageThisMascota = (m) => {
    const idDueño =
      m?.id_dueño ??
      m?.id_dueno ??
      m?.idDueño ??
      m?.idDueno ??
      m?.dueno?.id_usuario ??
      m?.dueno?.idUsuario ??
      m?.dueño?.id_usuario ??
      m?.dueño?.idUsuario
    return (isDueno && idDueño == myId) || isAdmin
  }

  const previewDuenosOptions = useMemo(() => {
    return (duenos || []).map((dueno) => ({
      value: dueno.id_usuario || dueno.idUsuario,
      label: `${dueno.nombre} ${dueno.apellido || ''} (${dueno.email})`.trim()
    }))
  }, [duenos])

  useEffect(() => {
    let mounted = true

    async function fetchMascota() {
      if (!mascotaId) {
        navigate('/mascotas', { replace: true })
        return
      }

      try {
        setLoading(true)
        setError(null)

        const resp = await mascotaService.getById(mascotaId)
        const data = resp?.data || resp

        if (!mounted) return

        if (!data) {
          setError('No se pudo cargar la mascota')
          return
        }

        setMascota(data)
        const idDuenoFromNested = data.dueno?.id_usuario || data.dueno?.idUsuario
        const petImage = normalizeFotoUrl(data.foto || data.avatar_url)
        setAvatarPreview(petImage || '/placeholder-pet.svg')
        setFormData({
          nombre: data.nombre || '',
          especie: data.especie || '',
          raza: data.raza || '',
          tipo_raza: data.tipo_raza || data.tipoRaza || '',
          fecha_nacimiento: normalizeDateToInput(data.fecha_nacimiento || data.fechaNacimiento),
          sexo: data.sexo || '',
          color: data.color || '',
          peso: data.peso ?? (data.peso_actual ?? ''),
          altura: data.altura ?? '',
          microchip: data.microchip || '',
          observaciones: data.observaciones || '',
          origen: data.origen || '',
          criadero: data.criadero || '',
          fecha_adopcion: normalizeDateToInput(data.fecha_adopcion || data.fechaAdopcion),
          id_dueño: (data.id_dueño ?? data.idDueño ?? idDuenoFromNested) ?? (user?.idUsuario ?? user?.id_usuario)
        })
      } catch (err) {
        if (!mounted) return
        setError(err?.response?.data?.error || err?.message || 'Error al cargar la mascota')
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchMascota()
    return () => {
      mounted = false
    }
  }, [mascotaId, navigate, user?.idUsuario, user?.id_usuario])

  useEffect(() => {
    let mounted = true
    async function fetchDuenos() {
      if (!isAdmin) return
      try {
        setLoadingDuenos(true)
        const response = await usuarioService.getAll({ tipo_usuario: 'DUEÑO', per_page: 100 })
        if (!mounted) return
        if (response?.success) {
          const items = response.data?.data || response.data?.data || response.data || []
          setDuenos(Array.isArray(items) ? items : [])
        } else {
          setDuenos(Array.isArray(response?.data) ? response.data : [])
        }
      } catch (err) {
        // Si no se puede cargar el catálogo de dueños, igual intentamos permitir la edición si no aplica.
        if (!mounted) return
        setDuenos([])
      } finally {
        if (mounted) setLoadingDuenos(false)
      }
    }

    fetchDuenos()
    return () => {
      mounted = false
    }
  }, [isAdmin])

  useEffect(() => {
    if (!error && !success) return
    const t = setTimeout(() => {
      setError(null)
      setSuccess(null)
    }, 2500)
    return () => clearTimeout(t)
  }, [error, success])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!mascotaId) return

    try {
      setSaving(true)
      setError(null)

      const { origen, criadero, fecha_adopcion, tipo_raza, ...rest } = formData

      if (origen === 'Comprado' && !String(criadero || '').trim()) {
        setError('Si la mascota fue comprada, indica el criadero, tienda o lugar de compra.')
        setSaving(false)
        return
      }

      const basePayload = {
        ...rest,
        peso: formData.peso !== '' && formData.peso !== null ? parseFloat(formData.peso) : null,
        altura: formData.altura !== '' && formData.altura !== null ? parseFloat(formData.altura) : null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        id_dueño: formData.id_dueño || user?.idUsuario || user?.id_usuario
      }

      // Mantener consistencia con Mascotas.jsx: si no hay tipo_raza, no enviarlo.
      if (tipo_raza) basePayload.tipo_raza = tipo_raza

      if (origen === 'Adoptado' || origen === 'Comprado') {
        basePayload.origen = origen
        basePayload.criadero = origen === 'Comprado' ? String(criadero || '').trim() : null
        if (fecha_adopcion) basePayload.fecha_adopcion = fecha_adopcion
      }

      const payload = avatarFile
        ? (() => {
            const fd = new FormData()
            Object.entries(basePayload).forEach(([k, v]) => {
              if (v === undefined) return
              if (v === null) {
                fd.append(k, '')
                return
              }
              fd.append(k, String(v))
            })
            fd.append('avatar', avatarFile, avatarFile.name)
            return fd
          })()
        : basePayload

      const resp = await mascotaService.update(mascotaId, payload)
      // Algunas respuestas llegan sin flag success pero con data válida.
      if (resp?.success === false) {
        setError(resp?.error || 'Error al actualizar la mascota')
        return
      }

      const updatedMascota = resp?.data || resp
      if (updatedMascota && typeof updatedMascota === 'object') {
        setMascota(updatedMascota)
        const newPreview = normalizeFotoUrl(updatedMascota.foto || updatedMascota.avatar_url) || '/placeholder-pet.svg'
        setAvatarPreview(newPreview)
      }
      setAvatarFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setSuccess('Mascota actualizada correctamente.')
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Error al actualizar la mascota')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAvatar = async () => {
    if (!mascotaId) return
    const ok = window.confirm('¿Eliminar la foto de la mascota?')
    if (!ok) return

    try {
      setDeletingAvatar(true)
      setError(null)
      await mascotaService.deleteAvatar(mascotaId)
      setAvatarFile(null)
      setAvatarPreview('/placeholder-pet.svg')
      setMascota((prev) => (prev ? { ...prev, foto: null, avatar_url: null } : prev))
      if (fileInputRef.current) fileInputRef.current.value = ''
      setSuccess('Foto eliminada correctamente.')
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'No se pudo eliminar la foto')
    } finally {
      setDeletingAvatar(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando mascota..." size="lg" />
  }

  if (!mascota) {
    return (
      <div className="space-y-4 animate-fade-in">
        {error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        ) : (
          <div className="text-gray-600">No se encontró la mascota.</div>
        )}
        <Button onClick={() => navigate('/mascotas')} variant="secondary">
          Volver a mascotas
        </Button>
      </div>
    )
  }

  if (!canManageThisMascota(mascota)) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          No tienes permisos para editar esta mascota (o la sesión no se pudo validar en PWA).
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.location.reload()} variant="secondary" type="button">
            Reintentar
          </Button>
          <Button onClick={() => navigate('/mascotas')} variant="secondary" type="button">
            Volver a mascotas
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold">Pet Records</p>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pastel-ink via-primary-600 to-pastel-ink bg-clip-text text-transparent">
            Editar Mascota
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-gray-500">
            {mascota?.nombre || '—'} · Actualiza sus datos de perfil
          </p>
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate('/mascotas')} disabled={saving}>
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="ml-4" type="button" aria-label="Cerrar error">
            <X className="w-5 h-5" />
          </button>
        </div>
      ) : null}
      {success ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-2xl flex items-center justify-between">
          <div>{success}</div>
          <button onClick={() => setSuccess(null)} className="ml-4" type="button" aria-label="Cerrar mensaje">
            <X className="w-5 h-5" />
          </button>
        </div>
      ) : null}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
          <div className="space-y-4">
            <Input
              label="Nombre"
              required
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
            />

            <Listbox
              label="Especie"
              options={[
                { value: '', label: 'Seleccione...' },
                { value: 'Perro', label: 'Perro' },
                { value: 'Gato', label: 'Gato' },
                { value: 'Conejo', label: 'Conejo' },
                { value: 'Ave', label: 'Ave' },
                { value: 'Hámster', label: 'Hámster' }
              ]}
              value={formData.especie}
              onChange={(value) => setFormData({ ...formData, especie: value })}
            />

            <Input
              label="Raza"
              value={formData.raza}
              onChange={(e) => setFormData({ ...formData, raza: e.target.value })}
            />

            <Listbox
              label="Tipo de raza"
              options={[
                { value: '', label: 'No indicado' },
                { value: 'pura', label: 'Pura' },
                { value: 'cruza', label: 'Cruza' },
                { value: 'mixto', label: 'Mixto' }
              ]}
              value={formData.tipo_raza}
              onChange={(value) => setFormData({ ...formData, tipo_raza: value })}
            />

            <Input
              label="Fecha de Nacimiento"
              type="date"
              value={formData.fecha_nacimiento}
              onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
            />

            <Listbox
              label="Sexo"
              options={[
                { value: '', label: 'Seleccione...' },
                { value: 'M', label: 'Macho' },
                { value: 'H', label: 'Hembra' }
              ]}
              value={formData.sexo}
              onChange={(value) => setFormData({ ...formData, sexo: value })}
            />

            <Input
              label="Color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            />

            <Input
              label="Peso (kg)"
              type="number"
              step="0.1"
              value={formData.peso}
              onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
            />

            <Input
              label="Altura (cm)"
              type="number"
              step="0.1"
              value={formData.altura}
              onChange={(e) => setFormData({ ...formData, altura: e.target.value })}
            />

            <Input
              label="Microchip"
              value={formData.microchip}
              onChange={(e) => setFormData({ ...formData, microchip: e.target.value })}
            />
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-pastel-pink/60 dark:border-gray-600 bg-white dark:bg-gray-800/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Foto de la mascota</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Subí una imagen (jpg/png/webp). Máximo 5MB.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-10 w-10 rounded-xl bg-[#0f1623] text-[#00c896] flex items-center justify-center"
                  aria-label="Seleccionar foto"
                >
                  <Camera className="w-5 h-5" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    if (!file.type.startsWith('image/')) {
                      setError('Selecciona una imagen válida.')
                      return
                    }
                    if (file.size > 5 * 1024 * 1024) {
                      setError('La imagen no debe superar 5MB.')
                      return
                    }
                    setError(null)
                    setAvatarFile(file)
                    const reader = new FileReader()
                    reader.onload = () => setAvatarPreview(reader.result)
                    reader.readAsDataURL(file)
                  }}
                />
              </div>

              <div className="mt-3 flex items-center gap-3">
                <div className="h-16 w-16 rounded-2xl border border-[#e8eef4] overflow-hidden bg-[#f8fafc] flex items-center justify-center">
                  <img
                    src={avatarPreview || '/placeholder-pet.svg'}
                    alt="Avatar mascota"
                    className="h-full w-full object-cover"
                    onError={(ev) => {
                      ev.currentTarget.src = '/placeholder-pet.svg'
                    }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {avatarFile ? avatarFile.name : 'Sin cambios'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {avatarFile ? `${Math.round(avatarFile.size / 1024)} KB` : 'Usando la imagen actual (si existe)'}
                  </p>
                </div>
                {avatarFile ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setAvatarFile(null)
                      setAvatarPreview(normalizeFotoUrl(mascota?.foto || mascota?.avatar_url) || '/placeholder-pet.svg')
                    }}
                  >
                    Quitar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={
                      deletingAvatar ||
                      saving ||
                      !(mascota?.foto || mascota?.avatar_url) ||
                      (avatarPreview || '').includes('/placeholder-pet.svg')
                    }
                    onClick={handleDeleteAvatar}
                  >
                    {deletingAvatar ? 'Eliminando...' : 'Eliminar'}
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-pastel-pink/60 dark:border-gray-600 bg-pastel-cream/30 dark:bg-gray-800/40 p-4 space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Procedencia</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  ¿Adoptado o comprado? Si fue compra, indicá criadero o tienda.
                </p>
              </div>

              <Listbox
                label="Origen"
                options={[
                  { value: '', label: 'No indicado' },
                  { value: 'Adoptado', label: 'Adoptado' },
                  { value: 'Comprado', label: 'Comprado' }
                ]}
                value={formData.origen}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    origen: value,
                    criadero: value === 'Comprado' ? formData.criadero : ''
                  })
                }
              />

              {formData.origen === 'Comprado' ? (
                <Input
                  label="Criadero, tienda o vendedor"
                  required
                  placeholder="Ej.: Criadero Los Robles, PetShop X…"
                  value={formData.criadero}
                  onChange={(e) => setFormData({ ...formData, criadero: e.target.value })}
                />
              ) : null}

              {formData.origen === 'Adoptado' || formData.origen === 'Comprado' ? (
                <Input
                  label={formData.origen === 'Adoptado' ? 'Fecha de adopción (opcional)' : 'Fecha de compra (opcional)'}
                  type="date"
                  value={formData.fecha_adopcion}
                  onChange={(e) => setFormData({ ...formData, fecha_adopcion: e.target.value })}
                />
              ) : null}
            </div>

            {isAdmin ? (
              <Listbox
                label="Dueño"
                options={[
                  { value: '', label: loadingDuenos ? 'Cargando...' : 'Seleccione un dueño...' },
                  ...previewDuenosOptions
                ]}
                value={formData.id_dueño}
                onChange={(value) => setFormData({ ...formData, id_dueño: value })}
                disabled={loadingDuenos}
              />
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Observaciones
              </label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                rows={5}
                className="input resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/mascotas')} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" loading={saving} disabled={saving}>
                Guardar cambios
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

